import { useCallback } from 'react';

import dayjs from 'dayjs';

import { PLAN_TYPE } from '../modals/modalTypes';
import { configs } from '../../../constants/configs/configs';
import { DATE_FORMAT_DASHED, dateMethods, adjustUTCToLocalTime } from '../../../utils/dates/dateMethods';
import { enrolledGroupCTASM } from '../stateMachine/stateMachine';
import { api } from '../../../modules/common-utils-services/axiosUtil';
import { cloneObject } from '../../../utils/miscellaneous/cloneObject';
import { startSpinner, stopSpinner } from '../../../actions/common/actionCreators';
import { APP_TYPES, FORMATTED_DATES } from '../../../constants/individualPortal/dashboard/dashboardModelTypes';
import {
    ICustomGroupingData,
    IDisenrollGroup,
    IGroup,
    IMember,
    IProcessCustomGroupingData,
    SaveCustomGroupingResponse,
    IAddMembersGroup,
    ICoverageDate,
    IGroupItems,
} from '../types/customGroupingTypes';
import {
    EnrolledGroupCTAButtonTypes,
    PLAN_EXPIRATION_WARNING,
    PlAN_TYPES,
    GroupCSRLevels,
} from '../../../constants/individualPortal/customGrouping/customGrouping.constants';
import { SPINNER_MESSAGES_IDS } from '../../../constants/common/miscellaneous';
import { ILocalDates } from '../../../utils/types/environment';
import { IMemberCardConfig } from '../components/common/MemberCard';
import { getDifferenceInDays } from '../../../utils/dates/dateMethods';

export default ({ dispatch }) => {
    const processCustomGroupingData = ({
        customGroupingData,
        environment,
        isHideDentalAPTCIfNoKids,
        setActiveTab,
        caseNumber,
        applicationYear,
    }: IProcessCustomGroupingData) => {
        const { groupList = [] }: { groupList: IGroup[] } = customGroupingData;

        const { stateCode, localDates } = environment;

        const enrolledGroupCTA = enrolledGroupCTASM(stateCode);

        const data: ICustomGroupingData = {
            caseNumber,
            applicationYear,
            healthGroups: [],
            dentalGroups: [],
            unEnrolledHealthGroup: [],
            unEnrolledDentalGroup: [],
            addMembersGroups: [],
            addDentalMembersGroups: [],
            showHealthPlansButtonDisabled: false,
            showDentalPlansButtonDisabled: false,
            isHealthRenewalGroupAvailable: false,
            isDentalRenewalGroupAvailable: false,
            allGroupsRenewal: false,
            anyAIANMember: isNativeAmerican(groupList),
            enableEndOfPHEForIND19: false,
            enableMe: false,
            unenrolledHealthGroupSelectedMembersCount: 0,
            isAllMembersEnrolledInHealthAndFinalized: false,
            isAllMembersEnrolledInDentalAndFinalized: false,
            isAnyHealthGroupEnrolled: false,
            isAnyDentalGroupEnrolled: false,
            healthGroupEnrolledMembersCount: 0,
            DentalGroupEnrolledMembersCount: 0,
            ...cloneObject(customGroupingData),
        };

        const groupsHashMap = new Map();

        data.groupList.forEach(groupItem => {
            const {
                groupType,
                groupEnrollmentId,
                coverageDate,
                memberList = [],
                allowDisenroll = false,
                maxAptc = 0,
            } = groupItem || {};

            if (groupEnrollmentId) groupsHashMap.set(groupEnrollmentId, groupItem);

            if (coverageDate) {
                const coverageStartDate = new Date(coverageDate);
                groupItem.validCoverageSDate =
                    allowDisenroll && validateCoverageStartDate({ coverageStartDate }, localDates);
            }

            let isEnrollmentCoverageDateWithinThisMonth = false;

            if (groupItem.enrollmentCreationDate) {
                const enrollmentDate = new Date(groupItem.enrollmentCreationDate);
                const serverDate = localDates?.serverDate;
                isEnrollmentCoverageDateWithinThisMonth =
                    enrollmentDate.getMonth() === serverDate?.month() &&
                    enrollmentDate.getFullYear() === serverDate?.year();
            }

            //state machine to get the CTA button data
            const ctaButton = enrolledGroupCTA(
                { customGroupingData, groupItem },
                { anyAIANMember: data.anyAIANMember, isEnrollmentCoverageDateWithinThisMonth },
            );

            groupItem.ctaButton = ctaButton;

            if (groupType === PlAN_TYPES.DENTAL) {
                data.showDentalPlansButtonDisabled = isDentalPlansButtonDisabled(groupItem, stateCode);
                if (groupEnrollmentId) {
                    // Process enrolled dental groups
                    data.enableMe = false;
                } else {
                    // Process unenrolled dental groups
                    data.enableMe = true;
                    data.unEnrolledDentalGroup.push(groupItem);
                }

                groupItem.isAllSelected = true;

                groupItem.showAptcDentalMessage = true;

                if (isHideDentalAPTCIfNoKids && maxAptc > 0) {
                    let kidsCount = 0;

                    memberList.forEach(member => {
                        if (member.ageAtCoverageDate < 19) {
                            kidsCount++;
                        }
                    });
                    if (kidsCount === 0) groupItem.showAptcDentalMessage = false;
                }

                data.dentalGroups.push(groupItem);
                if (groupItem.isRenewal) data.isDentalRenewalGroupAvailable = true;
            } else {
                //Health Groups
                if (groupItem.isRenewal) data.isHealthRenewalGroupAvailable = true;
                data.healthGroups.push(groupItem);

                if (!groupEnrollmentId) data.unEnrolledHealthGroup.push(groupItem);
            }
        });

        data.healthGroups.sort((a, b) => parseInt(a.groupEnrollmentId) - parseInt(b.groupEnrollmentId));

        if (data.isRenewalApplication && data.isHealthRenewalGroupAvailable) {
            const onlyhealth = data.healthGroups.filter(grp => grp.groupEnrollmentId);
            const renewalgroups = data.healthGroups.filter(grp => grp.isRenewal);
            if (renewalgroups.length > 0 && renewalgroups.length === onlyhealth.length) {
                data.allGroupsRenewal = true;
            }
        }

        data.addMembersGroups = handleAddMembers(data.unEnrolledHealthGroup, groupsHashMap);

        if (data.addMembersGroups.length > 0) {
            if (data.applicationType !== APP_TYPES.SEP) {
                data.addMembersGroups[0].AddedInEnrolledGroup.selectedMembersCount =
                    data.addMembersGroups[0].AddedInEnrolledGroup?.memberList.filter(x => !x.healthEnrollmentId).length;
            } else {
                data.addMembersGroups[0].AddedInEnrolledGroup.selectedMembersCount = 0;
            }
            // HIX-109818: Don't select any member, hence 0.
            data.addMembersGroups[0].enrollmentGroup.selectedMembersCount = 0;
        }

        data.hasAIMembers = isMixOfAIMembers({ healthGroups: data.healthGroups });

        let remainingUnenrolledHealthGroups: IGroup[] = [];

        data.healthGroups.forEach(group => {
            const isFoundInAddMembers = data.addMembersGroups.some(grp => grp.AddedInEnrolledGroup.id === group.id);
            if (!isFoundInAddMembers) {
                if (isAnyMembersUnEnrolledInHealth(group)) {
                    remainingUnenrolledHealthGroups.push(group);
                }
            }
        });

        remainingUnenrolledHealthGroups = sortUnenrolledGroups(
            remainingUnenrolledHealthGroups,
            data.keepNonStandardRelationshipsSeparate,
        );

        if (remainingUnenrolledHealthGroups.length > 0 && data.applicationType !== APP_TYPES.SEP) {
            data.unenrolledHealthGroupSelectedMembersCount = remainingUnenrolledHealthGroups[0].memberList.length;
            let uncheckGroupId: number;

            if (remainingUnenrolledHealthGroups.length > 0) {
                uncheckGroupId = remainingUnenrolledHealthGroups[0].id;
                let unenrolledHealthGroupMemberCount = 0;
                remainingUnenrolledHealthGroups[0].memberList.forEach(member => {
                    member.isChecked = true;
                    unenrolledHealthGroupMemberCount++;
                });
                let isCountUpdated = false;
                if (uncheckGroupId) {
                    data.addMembersGroups.forEach(addMemberGroup => {
                        if (addMemberGroup.enrollmentGroup.id === uncheckGroupId) {
                            addMemberGroup.enrollmentGroup.selectedMembersCount = unenrolledHealthGroupMemberCount;
                            isCountUpdated = true;
                            if (data.applicationType === APP_TYPES.OE) {
                                addMemberGroup.enrollmentGroup.memberList.forEach(member => {
                                    member.isChecked = true;
                                    isCountUpdated = false;
                                });
                            }
                        }
                    });
                }
                if (!isCountUpdated) {
                    remainingUnenrolledHealthGroups[0].selectedMembersCount = 0;
                }
            }
        }

        if (data.addMembersGroups.length > 0 && data.applicationType !== APP_TYPES.SEP) {
            data.addMembersGroups[0].AddedInEnrolledGroup.memberList.forEach(member => {
                member.isChecked = true;
            });
        }

        const { isAnyEnrolledGroups, enrolledMembersCount } = getEnrolledMembersCount(data.healthGroups);

        data.isAnyHealthGroupEnrolled = isAnyEnrolledGroups;
        data.healthGroupEnrolledMembersCount = enrolledMembersCount;

        const { isAnyDentalEnrolledGroups = false, enrolledDentalMembersCount = 0 } = isAnyDentalEnrollments(
            data.dentalGroups,
        );
        data.isAnyDentalGroupEnrolled = isAnyDentalEnrolledGroups;
        data.DentalGroupEnrolledMembersCount = enrolledDentalMembersCount;

        data.isAllMembersEnrolledInDental = isAllMembersEnrolledInDentalGroup(data.dentalGroups);

        data.isAllMembersEnrolledInHealthAndFinalized = isAllMembersEnrolledInGroupAndFinalized(
            data.healthGroups,
            PlAN_TYPES.HEALTH,
        );

        data.showHealthPlansButtonDisabled = true;

        // Here we are checking all the members in first group.
        const nonEnrolledGroups = getNonEnrolledGroups(data.healthGroups, PlAN_TYPES.HEALTH);

        if (nonEnrolledGroups.length > 0) {
            data.showHealthPlansButtonDisabled = false;
        }

        //Populate Unenrolled Dental Group
        data.addDentalMembersGroup = handleAddMembers(data.unEnrolledDentalGroup, groupsHashMap);

        if (data.addDentalMembersGroup.length > 0) {
            if (data.applicationType !== APP_TYPES.SEP) {
                data.addDentalMembersGroup[0].AddedInEnrolledGroup.selectedMembersCount =
                    data.addDentalMembersGroup[0].AddedInEnrolledGroup.memberList.filter(
                        x => !x.dentalEnrollmentId,
                    ).length;
            } else {
                data.addDentalMembersGroup[0].AddedInEnrolledGroup.selectedMembersCount = 0;
            }
            // HIX-109818: Don't select any member, hence 0.
            data.addDentalMembersGroup[0].enrollmentGroup.selectedMembersCount = 0;
        }

        let remainingUnenrolledDentalGroups: IGroup[] = [];

        data.dentalGroups.forEach(group => {
            const isFoundInAddMembers = data.addDentalMembersGroup.some(
                grp => grp.AddedInEnrolledGroup.id === group.id,
            );
            if (!isFoundInAddMembers) {
                if (isAnyMembersUnEnrolledInDental(group)) {
                    remainingUnenrolledDentalGroups.push(group);
                }
            }
        });

        remainingUnenrolledDentalGroups = sortUnenrolledGroups(
            remainingUnenrolledDentalGroups,
            data.keepNonStandardRelationshipsSeparate,
        );

        if (remainingUnenrolledDentalGroups.length > 0 && data.applicationType !== APP_TYPES.SEP) {
            let uncheckGroupId: number;

            if (remainingUnenrolledDentalGroups.length > 0) {
                uncheckGroupId = remainingUnenrolledDentalGroups[0].id;
                let unenrolledDentalGroupMemberCount = 0;
                remainingUnenrolledDentalGroups[0]?.memberList.forEach(member => {
                    member.isChecked = true;
                    unenrolledDentalGroupMemberCount++;
                });
                let isCountUpdated = false;
                if (uncheckGroupId) {
                    data.addDentalMembersGroup.forEach(addMemberGroup => {
                        if (addMemberGroup.enrollmentGroup.id === uncheckGroupId) {
                            addMemberGroup.enrollmentGroup.selectedMembersCount = unenrolledDentalGroupMemberCount;
                            isCountUpdated = true;
                            if (data.applicationType === APP_TYPES.OE) {
                                addMemberGroup.enrollmentGroup?.memberList.forEach(member => {
                                    member.isChecked = true;
                                    isCountUpdated = false;
                                });
                            }
                        }
                    });
                }
                if (!isCountUpdated) {
                    remainingUnenrolledDentalGroups[0].selectedMembersCount = 0;
                }
            }
        }

        if (data.addDentalMembersGroup.length > 0 && data.applicationType !== APP_TYPES.SEP) {
            data.addDentalMembersGroup[0].AddedInEnrolledGroup?.memberList.forEach(member => {
                member.isChecked = true;
            });
        }

        data.showDentalPlansButtonDisabled = true;

        // Here we are checking all the members in first group.
        const nonEnrolledDentalGroups = getNonEnrolledGroups(data.dentalGroups, PlAN_TYPES.DENTAL);

        if (nonEnrolledDentalGroups.length > 0) {
            data.showDentalPlansButtonDisabled = false;
        }

        data.isAllMembersEnrolledInDentalAndFinalized = isAllMembersEnrolledInGroupAndFinalized(
            data.dentalGroups,
            PlAN_TYPES.DENTAL,
        );

        if (
            data.isAllMembersEnrolledInHealthAndFinalized &&
            !data.isHealthRenewalGroupAvailable &&
            (!data.isAllMembersEnrolledInDental || data.isDentalRenewalGroupAvailable)
        ) {
            setActiveTab(1); // Set the active tab to Dental Plans
            if (data.isInsideOEEnrollmentWindow && data.applicationType === APP_TYPES.OE) {
                updateDentalActivePassiveRenewalGroups(data);
            }
        }

        return data;
    };

    const checkForPendingApplication = async ({ coverageYear }) => {
        const url = configs.endpoints.CHECK_FOR_PENDING_APPLICATION;
        const params = { coverageYear };
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const res = global.__ENV__.PRODUCTION ? await api.post(url, params) : await api.get(url);
            return res;
        } catch (e) {
            throw new Error(e);
        } finally {
            dispatch(stopSpinner());
        }
    };

    const disenrollIneligibleGroup = async ({
        caseNumber,
        group,
        typeOfPlan,
        coverageStartDate,
        serverDateInUTC,
        openModal,
    }) => {
        const { endpoints } = configs;
        const isHealthPlan = typeOfPlan === PlAN_TYPES.HEALTH;
        const url = isHealthPlan ? endpoints.TERMINATE_HEALTH_PLAN : endpoints.TERMINATE_DENTAL_PLAN;
        const serverDate = new Date(serverDateInUTC);
        const coverageSDate = new Date(coverageStartDate);

        // Determine the later date between serverDate and coverageSDate
        const laterDate = serverDate < coverageSDate ? coverageSDate : serverDate;

        // Format the later date according to the specified format
        const terminationDate = dayjs(laterDate).format(
            DATE_FORMAT_DASHED,
        );
        const enrollmentEndDate = dateMethods.formatDateusingIntl(
            serverDate < coverageSDate ? coverageStartDate : serverDate,
        );
        const params = {
            [isHealthPlan ? 'healthEnrollmentId' : 'dentalEnrollmentId']: group.groupEnrollmentId,
            caseNumber,
            reasonCode: 'NO_OPTION_SELECTED',
            terminationDate,
        };
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const res = global.__ENV__.PRODUCTION
                ? await api.post(url[typeOfPlan], params)
                : await api.get(url[typeOfPlan]);
            if (res.data === 'success') {
                openModal('disenrollIneligibleGroupModal', { enrollmentEndDate });
            } else {
                openModal('disenrollFailureModal');
            }
        } catch (error) {
            openModal('disenrollFailureModal');
        } finally {
            dispatch(stopSpinner());
        }
    };

    const disenrollPlan = async ({
        caseNumber,
        coverageStartDate,
        typeOfPlan,
        enrollmentId,
        group,
        enrollmentAction,
        serverDateInUTC,
        openModal,
    }) => {
        const currentDate = new Date();
        const coverageSDate = new Date(coverageStartDate);

        const disenrollGroup: IDisenrollGroup = {
            caseNumber,
            coverageStartDate,
            typeOfPlan,
            enrollmentId,
            disEnroll: true,
            isPlanEffectuated: group.isPlanEffectuated,
            endOfCurrentMonth: group.endOfCurrentMonth,
            endOfNextMonth: group.endOfNextMonth,
            endOfMonthAfterNext: group.endOfMonthAfterNext,
            hiosIssuerId: group.hiosIssuerId,
            planName: group.planName,
            logoUrl: '/hix/download/logo/' + group.hiosIssuerId,
            insideOEEnrollmentWindow: group.isInsideOEEnrollmentWindow,
            isIneligibleGroup: group.isIneligibleGroup,
            validCoverageDate: coverageSDate < currentDate,
        };

        if (group.isIneligibleGroup) {
            disenrollIneligibleGroup({ caseNumber, group, typeOfPlan, coverageStartDate, serverDateInUTC, openModal });
            return;
        }

        try {
            const res = await checkForPendingApplication({
                coverageYear: coverageSDate.getFullYear(),
            });
            if (res?.data) {
                openModal('pendingAppModal');
            } else {
                openModal('disenrollOptions', { typeOfPlan, enrollmentAction, group: disenrollGroup });
            }
        } catch (e) {
            openModal('disenrollOptions', { typeOfPlan, enrollmentAction, group: disenrollGroup });
        }
    };

    const submitDisenrollment = async ({ params }) => {
        const {
            disenrollOption,
            typeOfPlan,
            healthDetails,
            dentalDetails,
            disenrollGroup,
            disenrollDateSelected,
            isMemberLevelDisenrollEnabled,
            disenrollDateOptionSelected,
        } = params;

        let url: string, data;

        if (typeOfPlan === PLAN_TYPE.BOTH) {
            url = configs.endpoints.TERMINATE_PLAN;
            data = {
                healthEnrollmentId: healthDetails.enrollmentId,
                caseNumber: disenrollGroup.caseNumber,
                reasonCode: disenrollOption.reason,
                otherReason: disenrollOption.reasonOther,
                coverageStartDate: healthDetails.coverageStartDate,
                isRecTribe: false,
                ...(dentalDetails && dentalDetails.disEnroll
                    ? {
                          dentalEnrollmentId: dentalDetails.enrollmentId,
                      }
                    : {}),
            };
        } else if (typeOfPlan === PLAN_TYPE.HEALTH) {
            url = isMemberLevelDisenrollEnabled
                ? configs.endpoints.DISENROLL_MEMBERS
                : configs.endpoints.TERMINATE_HEALTH_PLAN;

            data = {
                reasonCode: disenrollOption.reason,
                otherReason: disenrollOption.reasonOther,
                caseNumber: disenrollGroup.caseNumber,
                ...(isMemberLevelDisenrollEnabled
                    ? {
                          planType: typeOfPlan,
                          enrollmentId: healthDetails.enrollmentId,
                      }
                    : {
                          healthEnrollmentId: healthDetails.enrollmentId,
                          coverageStartDate: healthDetails.coverageStartDate,
                          isRecTribe: false,
                      }),
                ...(dentalDetails && dentalDetails.disEnroll
                    ? {
                          dentalActive: true,
                          dentalAptc: dentalDetails.electedAptc,
                          dentalEnrollmentId: dentalDetails.enrollmentId,
                      }
                    : {}),
            };
        } else {
            url = isMemberLevelDisenrollEnabled
                ? configs.endpoints.DISENROLL_MEMBERS
                : configs.endpoints.TERMINATE_DENTAL_PLAN;
            data = {
                reasonCode: disenrollOption.reason,
                otherReason: disenrollOption.reasonOther,
                caseNumber: disenrollGroup.caseNumber,
                ...(isMemberLevelDisenrollEnabled
                    ? {
                          planType: typeOfPlan,
                          enrollmentId: dentalDetails.enrollmentId,
                      }
                    : {
                          coverageStartDate: dentalDetails.coverageStartDate,
                          dentalEnrollmentId: dentalDetails.enrollmentId,
                      }),
                ...(healthDetails && healthDetails.disEnroll && !isMemberLevelDisenrollEnabled
                    ? {
                          healthActive: true,
                      }
                    : {}),
            };
        }

        if (disenrollDateSelected) {
            data['terminationDateChoice'] = disenrollDateOptionSelected;
        }

        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const res = global.__ENV__.PRODUCTION ? await api.post(url, data) : await api.get(url);
            return res;
        } catch (e) {
            throw new Error(e);
        } finally {
            dispatch(stopSpinner());
        }
    };

    const saveCustomGrouping = async ({ caseNumber, params }) => {
        const url = configs.endpoints.SAVE_CUSTOM_GROUPING.processReplacements({
            caseNumber,
        });
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const res = global.__ENV__.PRODUCTION
                ? await api.post<SaveCustomGroupingResponse>(url, params)
                : await api.get<SaveCustomGroupingResponse>(url);
            return res;
        } catch (e) {
            throw new Error(e);
        } finally {
            dispatch(stopSpinner());
        }
    };

    const enrollAfterCustomGrouping = async (
        {
            isChangePlan,
            groupToEnroll,
            typeOfPlan,
            remainingAptc,
            remainingStateSubsidy,
            dentalRemainingAptc,
            caseNumber,
            enablePopupForEndOfPHE,
            nextAvailableDateSelected,
        },
        { openModal },
    ) => {
        let changePlanFlow =
            groupToEnroll.ctaButton.type === EnrolledGroupCTAButtonTypes.IS_CHANGE_PLAN && isChangePlan;

        const groupInEligibleMemberArray: IMember[] = [];
        const groupMemberArray: IMember[] = [];
        let enrollmentPriorApplicationId;
        const coverageDate = nextAvailableDateSelected || groupToEnroll.coverageDate;

        // Helper function to process members
        const processMembers = (members: IMember[], enrollmentKey, groupedArr) => {
            members.forEach(member => {
                if (member[enrollmentKey]) {
                    groupedArr.push(member);
                    enrollmentPriorApplicationId = Number(member[enrollmentKey]);
                } else if (!member.canBeAddedInEnrolledGroup) {
                    groupMemberArray.push(member);
                } else if (member?.canBeAddedInEnrolledGroup?.length) {
                    groupMemberArray.push(member);
                }
            });
        };

        let programType = 'H';

        // Process members based on plan type
        if (typeOfPlan === PlAN_TYPES.HEALTH) {
            processMembers(groupToEnroll.inEligibleMemberList, 'healthEnrollmentId', groupInEligibleMemberArray);
            processMembers(groupToEnroll.memberList, 'healthEnrollmentId', groupMemberArray);
        } else if (typeOfPlan === PlAN_TYPES.DENTAL) {
            processMembers(groupToEnroll.inEligibleMemberList, 'dentalEnrollmentId', groupInEligibleMemberArray);
            processMembers(groupToEnroll.memberList, 'dentalEnrollmentId', groupMemberArray);
            programType = 'D';
        }

        // Extracting required fields from groupToEnroll
        const { enrollmentEndDate, maxAptc, encData, maxStateSubsidy, groupEnrollmentId, isRenewal } = groupToEnroll;

        const params = {
            isChangePlanFlow: changePlanFlow,
            isSEPPlanFlow: false,
            enablePopupForEndOfPHE: false,
            group: {
                inEligibleMemberList: groupInEligibleMemberArray,
                memberList: groupMemberArray,
                groupType: typeOfPlan,
                programType,
                maxAptc,
                maxStateSubsidy,
                encData,
                isRenewal,
                groupEnrollmentId,
                coverageDate,
                enrollmentEndDate,
                remainingAptc,
                remainingStateSubsidy,
                enrollmentPriorApplicationId,
                dentalRemainingAptc,
            },
            dentalRemainingAptc,
        };

        try {
            const response = await saveCustomGrouping({ caseNumber, params });
            if (response?.data?.shoppingId) {
                location.href = configs.routes.SET_HOUSEHOLD + response?.data?.shoppingId;
                return;
            }
            if (response?.data?.message === PLAN_EXPIRATION_WARNING) {
                openModal('shopForNewPlan', { groupDetails: params, caseNumber });
                return;
            }
            openModal('customGroupingFailure', { customError: response?.data?.message });
        } catch (e) {
            openModal('customGroupingFailure', { customError: e });
        }
    };

    const shopForUnenrolledGroup = ({ group, enablePopupForEndOfPHE, isChangePlan, typeOfPlan, caseNumber }, args) => {
        const { openModal } = args;

        if (
            enablePopupForEndOfPHE &&
            [
                EnrolledGroupCTAButtonTypes.IS_BLANK,
                EnrolledGroupCTAButtonTypes.IS_FINALIZE_ENROLLMENT,
                EnrolledGroupCTAButtonTypes.IS_FINALIZE_PLAN,
                EnrolledGroupCTAButtonTypes.IS_CHANGE_PLAN,
            ].includes(group.ctaButton.type)
        ) {
            if (
                group.medicaidEndDate &&
                group.earliestStartDate &&
                (group.nextAvailableDate || group.nextCoverageYear)
            ) {
                openModal('endOfPHE', {
                    medicaidEndDate: group.medicaidEndDate,
                    grpForNewPlan: group,
                    typeOfPlan,
                    isChangePlan,
                });
                return;
            } else {
                enrollAfterCustomGrouping(
                    {
                        isChangePlan,
                        groupToEnroll: group,
                        typeOfPlan,
                        remainingAptc: group.remainingAptc,
                        remainingStateSubsidy: group.remainingStateSubsidy,
                        dentalRemainingAptc: group.dentalRemainingAptc,
                        caseNumber,
                        enablePopupForEndOfPHE: false,
                        nextAvailableDateSelected: group.nextAvailableDate,
                    },
                    args,
                );
            }
        } else {
            enrollAfterCustomGrouping(
                {
                    isChangePlan,
                    groupToEnroll: group,
                    typeOfPlan,
                    remainingAptc: group.remainingAptc,
                    remainingStateSubsidy: group.remainingStateSubsidy,
                    dentalRemainingAptc: group.dentalRemainingAptc,
                    caseNumber,
                    enablePopupForEndOfPHE: false,
                    nextAvailableDateSelected: group.nextAvailableDate,
                },
                args,
            );
        }
    };

    const addToExistingGroup = (
        { typeOfPlan, groupToChange, isChangePlan, enablePopupForEndOfPHE, caseNumber },
        args,
    ) => {
        const isPheScenario =
            groupToChange.medicaidEndDate &&
            groupToChange.earliestStartDate &&
            (groupToChange.nextAvailableDate || groupToChange.nextCoverageYear);

        if (enablePopupForEndOfPHE && isPheScenario) {
            shopForUnenrolledGroup(
                {
                    group: groupToChange,
                    enablePopupForEndOfPHE,
                    isChangePlan: false,
                    typeOfPlan,
                    caseNumber,
                },
                args,
            );
        } else {
            enrollAfterCustomGrouping(
                {
                    isChangePlan,
                    groupToEnroll: groupToChange,
                    typeOfPlan,
                    remainingAptc: groupToChange.remainingAptc,
                    remainingStateSubsidy: groupToChange.remainingStateSubsidy,
                    dentalRemainingAptc: groupToChange.dentalRemainingAptc,
                    caseNumber,
                    enablePopupForEndOfPHE,
                    nextAvailableDateSelected: groupToChange.nextAvailableDate,
                },
                args,
            );
        }
    };

    const changePlan = async ({ typeOfPlan, groupToChange, enablePopupForEndOfPHE, caseNumber }, args) => {
        const { openModal } = args;
        if (
            enablePopupForEndOfPHE &&
            [EnrolledGroupCTAButtonTypes.IS_FINALIZE_PLAN, EnrolledGroupCTAButtonTypes.IS_FINALIZE_ENROLLMENT].includes(
                groupToChange.ctaButton.type,
            )
        ) {
            shopForUnenrolledGroup(
                { typeOfPlan, group: groupToChange, enablePopupForEndOfPHE, isChangePlan: false, caseNumber },
                args,
            );
        } else {
            const isOverageMemberInGroup = hasOverageMember(groupToChange);
            if (isOverageMemberInGroup && groupToChange.ctaButton.type === EnrolledGroupCTAButtonTypes.IS_CHANGE_PLAN) {
                const isOnlyOverageMember = hasOnlyOverageMember(groupToChange);
                const isOverageAndOtherMember = hasOverageAndOtherMember(groupToChange);

                if (isOverageAndOtherMember) {
                    openModal('overageAndOtherMemberChangePlan', {
                        typeOfPlan,
                        groupToChange,
                        enablePopupForEndOfPHE,
                        caseNumber,
                    });
                    return;
                } else if (isOnlyOverageMember) {
                    openModal('overageMemberChangePlan');
                    return;
                } else {
                    addToExistingGroup(
                        {
                            typeOfPlan,
                            groupToChange,
                            isChangePlan: true,
                            enablePopupForEndOfPHE,
                            caseNumber,
                        },
                        args,
                    );
                }
            } else {
                addToExistingGroup(
                    {
                        typeOfPlan,
                        groupToChange,
                        isChangePlan: true,
                        enablePopupForEndOfPHE,
                        caseNumber,
                    },
                    args,
                );
            }
        }
    };

    const shopForNewPlan = async (groupDetails: any, applicationId: string, caseNumber: string, { openModal }) => {
        if (!groupDetails?.group) return;
        const newGroup = { ...groupDetails.group };

        newGroup.groupEnrollmentId = null;
        newGroup.enrollmentEndDate = null;

        if (applicationId !== newGroup.enrollmentPriorApplicationId) {
            newGroup.maxAptc = null;
            newGroup.maxStateSubsidy = null;
        } else if ((newGroup.remainingAptc || newGroup.remainingStateSubsidy) && groupDetails.isChangePlanFlow) {
            if (newGroup.remainingAptc) {
                newGroup.maxAptc = (newGroup.maxAptc || 0) + newGroup.remainingAptc;
            }
            if (newGroup.remainingStateSubsidy) {
                newGroup.maxStateSubsidy = (newGroup.maxStateSubsidy || 0) + newGroup.remainingStateSubsidy;
            }
        }

        newGroup.productType = newGroup.groupType === PLAN_TYPE.HEALTH ? 'H' : 'D';

        if (newGroup.memberList?.length) {
            newGroup.memberList.forEach((member: any) => {
                if (newGroup.groupType !== PLAN_TYPE.DENTAL && member.healthEnrollmentId) {
                    member.healthEnrollmentId = null;
                }
                if (member.dentalEnrollmentId) {
                    member.dentalEnrollmentId = null;
                }
            });
        }

        const updatedGroupDetails = { ...groupDetails, group: newGroup };

        try {
            const response = await saveCustomGrouping({ caseNumber, params: updatedGroupDetails });
            if (response?.data?.shoppingId) {
                location.href = configs.routes.SET_HOUSEHOLD + response?.data?.shoppingId;
                return;
            }
            if (response?.data?.message === PLAN_EXPIRATION_WARNING) {
                openModal('shopForNewPlan', { groupDetails: updatedGroupDetails, caseNumber });
                return;
            }
            openModal('customGroupingFailure', { customError: response?.data?.message });
        } catch (e) {
            openModal('customGroupingFailure', { customError: e });
        }
    };

    const isMixOfAIMembers = ({ healthGroups }) => {
        const groups = getNonEnrolledGroups(healthGroups, PlAN_TYPES.HEALTH);

        let aiangroupFound = false,
            non_aiangroupFound = false;

        groups.forEach(grp => {
            if (grp.groupCsr === GroupCSRLevels.CS2 || grp.groupCsr === GroupCSRLevels.CS3) {
                aiangroupFound = true;
            } else {
                non_aiangroupFound = true;
            }
        });

        return aiangroupFound && non_aiangroupFound;
    };

    const isAnyMembersUnEnrolledInHealth = (group: IGroup) => {
        return group.memberList.some(grp => !grp.healthEnrollmentId);
    };

    const isAnyMembersUnEnrolledInDental = (group: IGroup) => {
        return group.memberList.some(grp => !grp.dentalEnrollmentId);
    };

    const getNonEnrolledGroups = (group: IGroup[], planType: PlAN_TYPES): IGroup[] => {
        const nonEnrolledGroups: IGroup[] = [];
        group.forEach(group => {
            // skip enrolled groups
            let isEnrolledGroup = false;
            group.memberList.forEach(member => {
                if (
                    (planType === PlAN_TYPES.HEALTH && member.healthEnrollmentId) ||
                    (planType === PlAN_TYPES.DENTAL && member.dentalEnrollmentId)
                ) {
                    isEnrolledGroup = true;
                }
            });
            if (!isEnrolledGroup) {
                nonEnrolledGroups.push(group);
            }
        });

        return nonEnrolledGroups;
    };

    const handleAddMembers = (unEnrolledGroup: IGroup[], groupsHashMap) => {
        const addMembersGroupMap = new Map();

        unEnrolledGroup.forEach(group => {
            group.memberList.forEach(member => {
                const { canBeAddedInEnrolledGroup = [] } = member || {};
                if (canBeAddedInEnrolledGroup.length === 0) return;
                //Can be enrolled in the group
                canBeAddedInEnrolledGroup.forEach(enrolledGroupId => {
                    const canBeEnrolledInGroup = groupsHashMap.get(enrolledGroupId);
                    if (!canBeEnrolledInGroup) return;

                    // already have a group added in the map which has this custom group
                    const existingGroup = addMembersGroupMap.get(enrolledGroupId);
                    const existingGroupCloned = cloneObject(existingGroup);

                    canBeEnrolledInGroup.memberList.push(cloneObject(member));

                    if (existingGroup) {
                        const updatedGroup = existingGroupCloned;

                        updatedGroup.AddedInEnrolledGroup?.memberList.push(cloneObject(member));

                        addMembersGroupMap.set(enrolledGroupId, updatedGroup);
                    } else {
                        // Adding this group with the new member in the addMembersGroup map
                        addMembersGroupMap.set(enrolledGroupId, {
                            AddedInEnrolledGroup: cloneObject(canBeEnrolledInGroup),
                            enrollmentGroup: group,
                        });
                    }
                });
            });
        });

        return [...addMembersGroupMap.values()];
    };

    const isNativeAmerican = groupList => {
        for (let i = 0; i < groupList.length; i++) {
            for (let j = 0; j < groupList[i].memberList.length; j++) {
                if (groupList[i].memberList[j] && groupList[i].memberList[j].isNativeAmerican === 'Y') {
                    return true;
                }
            }
        }
        return false;
    };

    const validateCoverageStartDate = (
        { coverageStartDate, coverageEndDate }: ICoverageDate,
        localDates: ILocalDates,
    ): boolean => {
        const { serverDateInUTC, dstSavingsMs, timezoneOffsetMs } = localDates || {};
        const { sDate, sTime } = adjustUTCToLocalTime(serverDateInUTC, parseInt(timezoneOffsetMs), parseInt(dstSavingsMs));
        const currentDate = new Date(`${sDate} ${sTime}`);

        if (coverageEndDate) {
            const coverageEndDateFormatted = new Date(coverageEndDate);
            const coverageStartDateFormatted = new Date(coverageStartDate);
            return coverageEndDateFormatted < currentDate ? false : coverageStartDateFormatted <= currentDate;
        } else {
            const coverageStartDateFormatted = new Date(coverageStartDate);
            return coverageStartDateFormatted <= currentDate;
        }
    };

    const isDentalPlansButtonDisabled = (groupItem, stateCode) => {
        if (!groupItem.memberList) return true;

        for (let i = 0; i < groupItem.memberList.length; i++) {
            let memberInfo = groupItem.memberList[i];

            if (memberInfo.healthEnrollmentId && !memberInfo.dentalEnrollmentId) return false;
        }

        return true;
    };

    const getEnrolledMembersCount = (groups: IGroup[]) => {
        const enrolledGroups: IGroup[] = [];

        groups.forEach(group => {
            if (group.groupEnrollmentId) {
                enrolledGroups.push(group);
            }
        });

        let enrolledMembersCount = 0;

        enrolledGroups.forEach(group => {
            enrolledMembersCount += group?.memberList.filter(member => member.healthEnrollmentId).length;
        });

        return { isAnyEnrolledGroups: enrolledGroups.length > 0, enrolledMembersCount };
    };

    const isAnyDentalEnrollments = (dentalGroups: IGroup[]) => {
        let enrolledDentalMembersCount = 0;

        dentalGroups.forEach(group => {
            if (group.memberList) {
                var enrolledMembers = group.memberList.filter(x => x.dentalEnrollmentId);
                enrolledDentalMembersCount += enrolledMembers.length;
            }

            if (group.inEligibleMemberList) {
                var enrolledMembers = group.inEligibleMemberList.filter(x => x.dentalEnrollmentId);
                enrolledDentalMembersCount += enrolledMembers.length;
            }
        });

        return { isAnyDentalEnrolledGroups: enrolledDentalMembersCount > 0, enrolledDentalMembersCount };
    };

    const isAllMembersEnrolledInDentalGroup = (dentalGroups: IGroup[]) => {
        let totalMembersCount = 0;
        let enrolledMembersCount = 0;

        dentalGroups.forEach(group => {
            const enrolledMembers = group?.memberList.filter(x => x.dentalEnrollmentId);
            enrolledMembersCount += enrolledMembers.length;

            totalMembersCount += group.memberList.length;
        });

        return enrolledMembersCount === totalMembersCount;
    };

    const isAllMembersEnrolledInGroupAndFinalized = (groups: IGroup[], planType: PlAN_TYPES) => {
        let totalMembersCount = 0,
            enrolledMembersCount = 0,
            isPlanFinalized = true;

        groups.forEach(group => {
            const enrolledMembers = group?.memberList.filter(x => {
                if (planType === PlAN_TYPES.HEALTH) {
                    return x.healthEnrollmentId;
                } else {
                    return x.dentalEnrollmentId;
                }
            });
            enrolledMembersCount += enrolledMembers.length;

            totalMembersCount += group.memberList.length;

            if (group.enrollmentPriorApplicationId !== group.enrollmentApplicationId) {
                isPlanFinalized = false;
            }
        });

        return enrolledMembersCount === totalMembersCount && isPlanFinalized;
    };

    const sortUnenrolledGroups = (groups: IGroup[], keepNonStandardRelationshipsSeparate: boolean) => {
        return groups.sort((a, b) => {
            if (keepNonStandardRelationshipsSeparate) {
                return Number(a.isNonStandardRelationGroup) - Number(b.isNonStandardRelationGroup);
            } else {
                return Number(b.hasAPTCEligibleMembers) - Number(a.hasAPTCEligibleMembers);
            }
        });
    };

    const showAddAPTCMessage = (group: IGroupItems) => {
        if (group.isRenewal) return false;
        return (
            group.electedAptc !== group.maxAptc &&
            [EnrolledGroupCTAButtonTypes.IS_FINALIZE_ENROLLMENT, EnrolledGroupCTAButtonTypes.IS_FINALIZE_PLAN].includes(
                group.ctaButton.type,
            )
        );
    };

    const showAddStateSubsidyMessage = (group: IGroupItems) => {
        if (group.isRenewal) return false;

        return (
            group.electedStateSubsidy &&
            group.electedStateSubsidy !== group.maxStateSubsidy &&
            group.ctaButton.type === EnrolledGroupCTAButtonTypes.IS_FINALIZE_PLAN
        );
    };

    const hasOverageMember = (group: IGroupItems) => {
        return !!group.memberList.some(member => member.isOverageMember);
    };

    const hasOnlyOverageMember = (group: IGroupItems) => {
        return group.memberList.every(member => member.isOverageMember);
    };

    const hasOverageAndOtherMember = (group: IGroupItems) => {
        return hasOverageMember(group) && !hasOnlyOverageMember(group);
    };

    const areAllMembersAddedToGroups = (group: IGroupItems) => {
        return group.memberList.every(
            member => member?.canBeAddedInEnrolledGroup && member?.canBeAddedInEnrolledGroup.length,
        );
    };

    const getSelectedCount = (group: IMemberCardConfig[]) => {
        return group.filter(member => member.config.isChecked).length;
    };

    const getUniqueGroup = (group: IAddMembersGroup[]) => {
        const groupMap = new Map();

        group.forEach((group, index) => {
            const grpEnrollmentId = group.enrollmentGroup.id;
            groupMap.set(grpEnrollmentId?grpEnrollmentId:index, group);
        });

        return [...groupMap.values()];
    };

    const isHouseHoldAPTCEligible = (groups: IGroup[]) => {
        return groups.some(group => group?.maxAptc > 0);
    };

    const areAllMembersEnrolledInGroup = (groups: IGroup[]) => {
        return groups.every(group => {
            return group?.memberList.every(member => member.healthEnrollmentId);
        });
    };

    const updateDentalActivePassiveRenewalGroups = ({
        isRenewalApplication,
        isHealthRenewalGroupAvailable,
        isIneligibleMembersPresent,
        isDentalRenewalGroupAvailable,
        isDentalRenewed,
        groupList,
        dentalGroups,
    }: {
        isRenewalApplication: boolean;
        isHealthRenewalGroupAvailable: boolean;
        isIneligibleMembersPresent: boolean;
        isDentalRenewalGroupAvailable: boolean;
        isDentalRenewed: boolean;
        groupList: IGroupItems[];
        dentalGroups: IGroup[];
    }) => {
        const isDentalActiveRenewal =
            ((isRenewalApplication && isHealthRenewalGroupAvailable) ||
                !isAllEnrolledInHealth(dentalGroups?.[0]) ||
                isIneligibleMembersPresent) &&
            isDentalRenewalGroupAvailable;
        const isDentalPassiveRenewed =
            (!isAllEnrolledInHealth(dentalGroups?.[0]) || isIneligibleMembersPresent) &&
            !isDentalRenewalGroupAvailable &&
            isDentalEnrollmentPresent(dentalGroups) &&
            isDentalRenewed;

        if (isDentalActiveRenewal || isDentalPassiveRenewed) {
            const groupsList = groupList.map(group => {
                if (group.groupType !== PlAN_TYPES.DENTAL) return group;
                if (
                    isDentalActiveRenewal &&
                    group.ctaButton.type === EnrolledGroupCTAButtonTypes.IS_RENEW_OR_CHANGE_PLAN
                ) {
                    group.ctaButton.type = EnrolledGroupCTAButtonTypes.IS_BLANK;
                } else if (
                    isDentalPassiveRenewed &&
                    group.ctaButton.type === EnrolledGroupCTAButtonTypes.IS_CHANGE_PLAN
                ) {
                    group.ctaButton.type = EnrolledGroupCTAButtonTypes.IS_BLANK;
                }
            });
            return groupsList;
        }
    };

    const isAllEnrolledInHealth = group => {
        return group.memberList.every(group => {
            return group.healthEnrollmentId;
        });
    };

    const isDentalEnrollmentPresent = groups => {
        return groups.some(group => group.memberList.some(member => member.dentalEnrollmentId));
    };

    const calculateRemainingDays = ({
        oepEndDate,
        sepEndDate,
        serverDate,
    }: {
        oepEndDate: Date;
        sepEndDate: Date;
        serverDate: Date;
    }) => {
        if (sepEndDate) {
            return getDifferenceInDays(serverDate, sepEndDate)+1;
        } else {
            return getDifferenceInDays(serverDate, oepEndDate);
        }
    };

    const getMaxAptc = (group: IGroup, planType: PlAN_TYPES) => {
        if (group.maxAptc !== undefined && group.hasAPTCEligibleMembers) {
            var aptc = 0;

            var anyMemberWithoutAptc = false;
            var doNotAddGroupMaxAptc = true;

            group.memberList.forEach(member => {
                if (member.aptc && member.dentalEnrollmentId === undefined) {
                    aptc += parseFloat(member.aptc.toFixed(2));
                } else {
                    anyMemberWithoutAptc = true;
                    if (
                        ((planType === PlAN_TYPES.HEALTH && !member.healthEnrollmentId) ||
                            (planType === PlAN_TYPES.DENTAL && !member.dentalEnrollmentId)) &&
                        member.canBeAddedInEnrolledGroup &&
                        member.canBeAddedInEnrolledGroup.length > 0
                    ) {
                        doNotAddGroupMaxAptc = false;
                    }
                }
            });

            if (anyMemberWithoutAptc && group.maxAptc && doNotAddGroupMaxAptc) {
                aptc += parseFloat(group.maxAptc.toFixed(2));
            }

            return aptc;
        }
    };

    const getMaxStateSubsidy = (group: IGroup) => {
        if (group.maxStateSubsidy !== undefined && group.hasStateSubsidyEligibleMembers == true) {
            var stateSubsidy = 0;
            var anyMemberWithoutStateSubsidy = false;
            group.memberList.forEach(member => {
                if (member.stateSubsidy) {
                    stateSubsidy += parseFloat(member.stateSubsidy.toFixed(2));
                } else {
                    anyMemberWithoutStateSubsidy = true;
                }
            });

            if (anyMemberWithoutStateSubsidy && group.maxStateSubsidy) {
                stateSubsidy += parseFloat(group.maxStateSubsidy.toFixed(2));
            }

            return stateSubsidy;
        }
    };

    const isGroupSubsidized = (group: IGroup) => {
        let isGroupSubsidized = group.hasAPTCEligibleMembers;

        if (group.hasStateSubsidyEligibleMembers) {
            isGroupSubsidized = isGroupSubsidized || group.hasStateSubsidyEligibleMembers;
        }

        return isGroupSubsidized;
    };

    const processGroupWarning = (previousGroup: IGroup | undefined, selectedGroup: IGroup, isNonStandardGroupFailureModalVisible: boolean) => {
        if(!previousGroup?.id || previousGroup?.id === selectedGroup?.id || !previousGroup?.memberList?.length) return { showMultiGroupFailureModal: false, modalProps: {} };

        const isPreviousGroupSubsidized = isGroupSubsidized(previousGroup);
        const isSelectedGroupSubsidized = isGroupSubsidized(selectedGroup);

        const isPreviousGroupHasNativeAmericanMembers = isNativeAmerican([previousGroup]);
        const isSelectedGroupHasNativeAmericanMembers = isNativeAmerican([selectedGroup]);

        const isPreviousGroupNonStandard = previousGroup?.isNonStandardRelationGroup;
        const isSelectedGroupNonStandard = selectedGroup?.isNonStandardRelationGroup;

        const isPreviousGroupedHasOverageMember = hasOverageMember(previousGroup);
        const isSelectedGroupHasOverageMember = hasOverageMember(selectedGroup);

        let modalProps = {
            multiGroupFailureNonStandardNotAllowed: false,
            multiGroupFailureNotAllowed: false,
            multiGroupFailureOverageMember: false,
            multiGroupFailureChangeRecommendedNativeAmerican: false,
            multiGroupFailureChangeRecommended: false,
        };
        let showMultiGroupFailureModal = false;

        if (
            (isPreviousGroupNonStandard && isSelectedGroupNonStandard) ||
            isPreviousGroupNonStandard !== isSelectedGroupNonStandard
        ) {
            showMultiGroupFailureModal = true;
            modalProps.multiGroupFailureNonStandardNotAllowed = true;
        }

        if (isPreviousGroupSubsidized === isSelectedGroupSubsidized) {
            showMultiGroupFailureModal = true;

            if (previousGroup?.groupCsr !== selectedGroup?.groupCsr) {
                const isPreviousGroupInCSR = [GroupCSRLevels.CS2, GroupCSRLevels.CS3, GroupCSRLevels.CS4].includes(
                    previousGroup?.groupCsr,
                );
                const isSelectedGroupInCSR = [GroupCSRLevels.CS2, GroupCSRLevels.CS3, GroupCSRLevels.CS4].includes(
                    previousGroup?.groupCsr,
                );

                if (
                    (isPreviousGroupHasNativeAmericanMembers || isSelectedGroupHasNativeAmericanMembers) &&
                    (isPreviousGroupInCSR || isSelectedGroupInCSR)
                ) {
                    modalProps.multiGroupFailureChangeRecommendedNativeAmerican = true;
                } else {
                    modalProps.multiGroupFailureChangeRecommended = true;
                }
            }
        } else {
            showMultiGroupFailureModal = true;
            modalProps.multiGroupFailureNotAllowed = !isNonStandardGroupFailureModalVisible;
        };

        if((isPreviousGroupedHasOverageMember && isSelectedGroupHasOverageMember) || (isPreviousGroupedHasOverageMember !== isSelectedGroupHasOverageMember)){
            showMultiGroupFailureModal = true;
            modalProps.multiGroupFailureOverageMember = true;
        };

        return { showMultiGroupFailureModal, modalProps };
    }

    return {
        processCustomGroupingData: useCallback(processCustomGroupingData, []),
        disenrollPlan: useCallback(disenrollPlan, []),
        submitDisenrollment: useCallback(submitDisenrollment, []),
        changePlan: useCallback(changePlan, []),
        enrollAfterCustomGrouping: useCallback(enrollAfterCustomGrouping, []),
        shopForNewPlan: useCallback(shopForNewPlan, []),
        addToExistingGroup: useCallback(addToExistingGroup, []),
        checkForPendingApplication: useCallback(checkForPendingApplication, []),
        shopForUnenrolledGroup: useCallback(shopForUnenrolledGroup, []),
        showAddAPTCMessage: useCallback(showAddAPTCMessage, []),
        showAddStateSubsidyMessage: useCallback(showAddStateSubsidyMessage, []),
        areAllMembersAddedToGroups: useCallback(areAllMembersAddedToGroups, []),
        getSelectedCount: useCallback(getSelectedCount, []),
        getUniqueGroup: useCallback(getUniqueGroup, []),
        isHouseHoldAPTCEligible: useCallback(isHouseHoldAPTCEligible, []),
        areAllMembersEnrolledInGroup: useCallback(areAllMembersEnrolledInGroup, []),
        calculateRemainingDays: useCallback(calculateRemainingDays, []),
        getMaxAptc: useCallback(getMaxAptc, []),
        getMaxStateSubsidy: useCallback(getMaxStateSubsidy, []),
        isAnyMembersUnEnrolledInHealth: useCallback(isAnyMembersUnEnrolledInHealth, []),
        isAnyMembersUnEnrolledInDental: useCallback(isAnyMembersUnEnrolledInDental, []),
        isAllEnrolledInHealth: useCallback(isAllEnrolledInHealth, []),
        updateDentalActivePassiveRenewalGroups: useCallback(updateDentalActivePassiveRenewalGroups, []),
        processGroupWarning: useCallback(processGroupWarning, []),
    };
};
